import React, { useState, useEffect, useRef } from 'react';
import { signIn } from '../utils/authApi';

const BACKEND = import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com';

function CashierScanLogin({ onAuth, onClose }: { onAuth: (user: any, token: string) => void; onClose: () => void }) {
  const [error, setError] = useState('');
  const [scanning, setScanning] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  useEffect(() => {
    let interval: any;
    navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } })
      .then(stream => {
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
        setScanning(true);
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d')!;
        interval = setInterval(() => {
          const v = videoRef.current;
          if (!v || v.readyState < 2) return;
          canvas.width = v.videoWidth; canvas.height = v.videoHeight;
          ctx.drawImage(v, 0, 0);
          const jsQR = (window as any).jsQR;
          if (!jsQR) return;
          const code = jsQR(ctx.getImageData(0, 0, canvas.width, canvas.height).data, canvas.width, canvas.height);
          if (code?.data?.startsWith('CASHIER_TOKEN:')) {
            clearInterval(interval); stopStream();
            const badgeToken = code.data.replace('CASHIER_TOKEN:', '');
            fetch(`${BACKEND}/auth/cashier-login`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ badge_token: badgeToken }),
            })
              .then(r => r.json())
              .then(({ access_token }) =>
                fetch(`${BACKEND}/auth/me`, { headers: { Authorization: `Bearer ${access_token}` } })
                  .then(r => r.json())
                  .then(user => onAuth(user, access_token))
              )
              .catch(() => setError('Invalid or expired badge.'));
          }
        }, 300);
      })
      .catch(() => setError('Camera access denied.'));
    return () => { clearInterval(interval); stopStream(); };
  }, []);

  const stopStream = () => streamRef.current?.getTracks().forEach(t => t.stop());

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{
  background: '#fff',
  borderRadius: 16,
  padding: 24,
  width: 320,
  maxWidth: 'calc(100vw - 32px)',
  boxSizing: 'border-box',
  textAlign: 'center',
  fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
}}>
        <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 4 }}>📷 Scan Cashier Badge</div>
        <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 16 }}>Point camera at your QR badge</div>
        <div style={{ borderRadius: 12, overflow: 'hidden', background: '#000', marginBottom: 16, position: 'relative', height: 220 }}>
          <video ref={videoRef} autoPlay playsInline muted style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          {scanning && (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
              <div style={{ width: 140, height: 140, border: '3px solid #3b82f6', borderRadius: 12, boxShadow: '0 0 0 9999px rgba(0,0,0,0.4)' }} />
            </div>
          )}
        </div>
        {error && <div style={{ background: '#fef2f2', color: '#dc2626', borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 12 }}>{error}</div>}
        <input
          type="file"
          accept="image/*"
          capture="environment"
          style={{ display: 'none' }}
          id="badge-photo-input"
          onChange={async e => {
            const file = e.target.files?.[0];
            if (!file) return;
            const img = new Image();
            const url = URL.createObjectURL(file);
            img.onload = () => {
              const canvas = document.createElement('canvas');
              canvas.width = img.width; canvas.height = img.height;
              const ctx = canvas.getContext('2d')!;
              ctx.drawImage(img, 0, 0);
              URL.revokeObjectURL(url);
              const jsQR = (window as any).jsQR;
              if (!jsQR) return setError('QR scanner not loaded.');
              const code = jsQR(ctx.getImageData(0, 0, img.width, img.height).data, img.width, img.height);
              if (code?.data?.startsWith('CASHIER_TOKEN:')) {
                stopStream();
                const badgeToken = code.data.replace('CASHIER_TOKEN:', '');
                fetch(`${BACKEND}/auth/cashier-login`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ badge_token: badgeToken }),
                })
                  .then(r => r.json())
                  .then(({ access_token }) =>
                    fetch(`${BACKEND}/auth/me`, { headers: { Authorization: `Bearer ${access_token}` } })
                      .then(r => r.json())
                      .then(user => onAuth(user, access_token))
                  )
                  .catch(() => setError('Invalid or expired badge.'));
              } else {
                setError('QR not recognized. Try the live camera instead.');
              }
            };
            img.src = url;
          }}
        />
        <label htmlFor="badge-photo-input" style={{ display: 'block', width: '100%', padding: '8px', borderRadius: 8, border: '1px solid #3b82f6', background: '#eff6ff', color: '#1d4ed8', fontSize: 12, fontWeight: 700, cursor: 'pointer', marginBottom: 8, boxSizing: 'border-box', textAlign: 'center' }}>
          📸 Take Photo of Badge
        </label>
        <input
          placeholder="Or paste CASHIER_TOKEN:... here"
          style={{ width: '100%', padding: '8px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 11, marginBottom: 8, boxSizing: 'border-box' }}
          onChange={async e => {
            const val = e.target.value.trim();
            if (val.startsWith('CASHIER_TOKEN:')) {
              stopStream();
              const badgeToken = val.replace('CASHIER_TOKEN:', '');
              try {
                const loginRes = await fetch(`${BACKEND}/auth/cashier-login`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ badge_token: badgeToken }),
                });
                if (!loginRes.ok) throw new Error();
                const { access_token } = await loginRes.json();
                const res = await fetch(`${BACKEND}/auth/me`, { headers: { Authorization: `Bearer ${access_token}` } });
                const user = await res.json();
                onAuth(user, access_token);
              } catch { setError('Invalid or expired badge.'); }
            }
          }}
        />
        <button onClick={() => { stopStream(); onClose(); }} style={{ width: '100%', padding: '10px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#f9fafb', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}>
          Cancel
        </button>
      </div>
    </div>
  );
}

interface Props {
  onAuth: (user: any, token: string) => void;
}

export default function TeamLoginScreen({ onAuth }: Props) {
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      *::-webkit-scrollbar { width: 0px; height: 0px; display: none; }
      * { scrollbar-width: none; -ms-overflow-style: none; }
    `;
    document.head.appendChild(style);
    return () => { document.head.removeChild(style); };
  }, []);
  const [prefix, setPrefix] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showScan, setShowScan] = useState(false);

  // Clear any stale tab left in localStorage by a previous user on this
  // browser/device, so the post-login redirect logic in App.tsx isn't
  // overridden by e.g. 'employees' left behind by the owner's last session.
  const handleAuthSuccess = (user: any, token: string) => {
    localStorage.removeItem('cloudairambo_last_tab');
    onAuth(user, token);
  };

  const handleLogin = async () => {
    if (!prefix.trim() || !password) { setError('All fields required.'); return; }
    setLoading(true);
    setError('');
    try {
      const result = await signIn(`${prefix.trim()}@cloudairambo.com`, password);
      handleAuthSuccess(result.user, result.access_token);
    } catch (e: any) {
      setError(e.message || 'Invalid credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', boxSizing: 'border-box' }}>
      <div style={{
  background: '#fff',
  borderRadius: '16px',
  padding: '36px',
  width: '480px',
  maxWidth: '100%',
  boxSizing: 'border-box',
  boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
  border: '1px solid #e5e7eb',
  position: 'relative',
  fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
}}>
        <div style={{ textAlign: 'center', marginBottom: '28px' }}>
          <div style={{ width: '44px', height: '44px', background: 'linear-gradient(135deg, #3b82f6, #9333ea)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 800, fontSize: '14px', margin: '0 auto 12px' }}>CA</div>

          <button
  onClick={() => { window.location.href = '/'; }}
  style={{ position: 'absolute', top: '16px', right: '16px', background: 'none', border: 'none', fontSize: '22px', cursor: 'pointer', color: '#9ca3af', lineHeight: 1 }}
  title="Go back"
>
  &times;
</button>
          <h1 style={{ margin: 0, fontSize: '20px', fontWeight: 800, color: '#111827' }}>Team Login</h1>
          <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#6b7280' }}>CloudAIRambo Staff Portal</p>
        </div>

        <div style={{ marginBottom: '14px' }}>
          <label style={{ fontSize: '11px', fontWeight: 700, color: '#374151', display: 'block', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Email</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden' }}>
            <input
              style={{ flex: 1, minWidth: 0, padding: '9px 12px', border: 'none', outline: 'none', fontSize: '13px', color: '#111827', background: '#f9fafb' }}
              placeholder="john"
              value={prefix}
              onChange={e => setPrefix(e.target.value.toLowerCase().replace(/[^a-z0-9._-]/g, ''))}
              onKeyDown={e => e.key === 'Enter' && handleLogin()}
            />
            <span style={{ padding: '0 12px', background: '#f1f5f9', fontSize: '12px', color: '#6b7280', display: 'flex', alignItems: 'center', borderLeft: '1px solid #e5e7eb', whiteSpace: 'nowrap' }}>
              @cloudairambo.com
            </span>
          </div>
        </div>

        <div style={{ marginBottom: '20px' }}>
          <label style={{ fontSize: '11px', fontWeight: 700, color: '#374151', display: 'block', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Password</label>
          <input
            type="password"
            style={{ width: '100%', padding: '9px 12px', border: '1px solid #e5e7eb', borderRadius: '8px', outline: 'none', fontSize: '13px', color: '#111827', background: '#f9fafb', boxSizing: 'border-box' }}
            placeholder="••••••••"
            value={password}
            onChange={e => setPassword(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleLogin()}
          />
        </div>

        {error && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', borderRadius: '6px', padding: '8px 12px', fontSize: '12px', fontWeight: 600, marginBottom: '14px' }}>{error}</div>}

        <button
          onClick={handleLogin}
          disabled={loading}
          style={{ width: '100%', padding: '11px', background: 'linear-gradient(to right, #3b82f6, #9333ea)', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 700, fontSize: '14px', cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1 }}
        >
          {loading ? 'Signing in...' : 'Sign In'}
        </button>
        <p style={{ textAlign: 'center', fontSize: '12px', color: '#9ca3af', marginTop: '16px' }}>
          Forgot your password? Contact your <strong style={{ color: '#6b7280' }}>administrator</strong>.
        </p>
        <button
          onClick={() => setShowScan(true)}
          style={{ width: '100%', marginTop: '12px', padding: '10px', borderRadius: '8px', border: '2px dashed #e5e7eb', background: 'transparent', color: '#6b7280', fontWeight: 700, fontSize: '13px', cursor: 'pointer' }}
        >
          📷 Scan Badge to Login
        </button>
        {showScan && <CashierScanLogin onAuth={handleAuthSuccess} onClose={() => setShowScan(false)} />}
      </div>
    </div>
  );
}
